import React, { useEffect, useState, useCallback } from "react";
import { Bell, Trash2, Power, Pencil } from "lucide-react";

const API = "https://connorvps.com/ggp-courts/watches";

function fmtCreated(s) {
  if (!s) return "";
  // SQLite default CURRENT_TIMESTAMP is UTC, no timezone marker — add Z.
  const d = new Date(s.includes("T") ? s : s.replace(" ", "T") + "Z");
  return isNaN(d) ? s : d.toLocaleString();
}

// "07:30" → "7:30 AM"
function to12h(t24) {
  if (!t24) return "";
  const [h, m] = t24.split(":").map((s) => parseInt(s, 10));
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

// Summarize a watch's slot set by grouping by date and showing
// "MMM D: 8:00 AM, 8:30 AM, 9:00 AM" per date.
function summarizeSlots(slotsJson) {
  let slots = [];
  try { slots = JSON.parse(slotsJson || "[]"); } catch { return ""; }
  const byDate = new Map();
  for (const s of slots) {
    const [date, t24] = s.split("|");
    if (!date || !t24) continue;
    if (!byDate.has(date)) byDate.set(date, []);
    byDate.get(date).push(t24);
  }
  const lines = Array.from(byDate.entries()).sort();
  return lines
    .map(([date, times]) => {
      const [, mo, d] = date.split("-");
      const label = `${parseInt(mo, 10)}/${parseInt(d, 10)}`;
      const t = times.sort().map(to12h).join(", ");
      return `${label}: ${t}`;
    })
    .join("  ·  ");
}

const Watches = ({ refreshKey, onEditWatch }) => {
  const [watches, setWatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(API);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setWatches(await r.json());
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this reminder?")) return;
    await fetch(`${API}/${id}`, { method: "DELETE" });
    await load();
  };

  const onToggle = async (id) => {
    await fetch(`${API}/${id}/toggle`, { method: "POST" });
    await load();
  };

  return (
    <div className="max-w-6xl mx-auto p-4 pt-2 pb-40">
      <div className="flex items-center mb-4">
        <Bell className="text-gray-600 mr-2" />
        <h2 className="text-2xl font-bold">Your Reminders</h2>
      </div>

      {loading && <p className="text-gray-500">Loading…</p>}
      {error && <p className="text-red-600">Failed to load: {error}</p>}

      {!loading && watches.length === 0 && (
        <p className="text-gray-500 italic">
          No reminders yet. Click cells in the matrix above, then use the toolbar to save one.
        </p>
      )}

      {watches.length > 0 && (
        <div className="overflow-x-auto bg-white shadow rounded-lg">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="p-2 border-b">Status</th>
                <th className="p-2 border-b">Label</th>
                <th className="p-2 border-b">Duration</th>
                <th className="p-2 border-b">Min courts</th>
                <th className="p-2 border-b">Slots</th>
                <th className="p-2 border-b">Email</th>
                <th className="p-2 border-b">Created</th>
                <th className="p-2 border-b text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {watches.map((w) => {
                let slotCount = 0;
                try { slotCount = (JSON.parse(w.slots_json) || []).length; } catch {}
                let durArr = [w.duration_minutes];
                try {
                  const parsed = JSON.parse(w.durations_json);
                  if (Array.isArray(parsed) && parsed.length > 0) durArr = parsed;
                } catch {}
                durArr = durArr.slice().sort((a, b) => a - b);
                return (
                  <tr key={w.id} className={w.active ? "" : "opacity-50"}>
                    <td className="p-2 border-b">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                        w.active ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-600"
                      }`}>
                        {w.active ? "active" : "paused"}
                      </span>
                    </td>
                    <td className="p-2 border-b">
                      {w.label || <em className="text-gray-400">—</em>}
                    </td>
                    <td className="p-2 border-b font-mono text-xs whitespace-nowrap">
                      {durArr.join(" / ")} min
                    </td>
                    <td className="p-2 border-b">{w.min_courts}</td>
                    <td className="p-2 border-b text-xs text-gray-600">
                      <div className="flex items-center gap-2">
                        <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-[11px]">
                          {slotCount}
                        </span>
                        <span className="truncate max-w-md" title={summarizeSlots(w.slots_json)}>
                          {summarizeSlots(w.slots_json)}
                        </span>
                      </div>
                    </td>
                    <td className="p-2 border-b text-xs">{w.email}</td>
                    <td className="p-2 border-b text-xs text-gray-500">{fmtCreated(w.created_at)}</td>
                    <td className="p-2 border-b text-right whitespace-nowrap">
                      <button
                        onClick={() => onEditWatch && onEditWatch(w)}
                        title="Edit"
                        className="text-gray-600 hover:text-blue-600 mr-2"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => onToggle(w.id)}
                        title={w.active ? "Pause" : "Activate"}
                        className="text-gray-600 hover:text-gray-900 mr-2"
                      >
                        <Power size={16} />
                      </button>
                      <button
                        onClick={() => onDelete(w.id)}
                        title="Delete"
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Watches;
